// Import shared currency constants
import { CURRENCIES } from '../constants/currencies';

// Re-export CURRENCIES for convenience
export { CURRENCIES };

// Format currency amount
export const formatCurrency = (amount, currency = 'USD') => {
  const currencyInfo = CURRENCIES.find(c => c.code === currency);
  const numericAmount = Number(amount) || 0;
  return `${currencyInfo?.symbol || '$'}${numericAmount.toFixed(2)}`;
};

// Get currency symbol
export const getCurrencySymbol = (currency = 'USD') => {
  const currencyInfo = CURRENCIES.find(c => c.code === currency);
  return currencyInfo?.symbol || '$';
};

// Get currency info
export const getCurrencyInfo = (currency = 'USD') => {
  return CURRENCIES.find(c => c.code === currency) || CURRENCIES[0];
};

// Simple conversion rates (in a real app, these would come from an API)
const EXCHANGE_RATES = {
  'USD': { 'EUR': 0.85, 'GBP': 0.73, 'CAD': 1.25, 'AUD': 1.35, 'JPY': 110.00, 'CHF': 0.92, 'CNY': 6.45, 'INR': 75.00 },
  'EUR': { 'USD': 1.18, 'GBP': 0.86, 'CAD': 1.47, 'AUD': 1.59, 'JPY': 129.41, 'CHF': 1.08, 'CNY': 7.59, 'INR': 88.24 },
  'GBP': { 'USD': 1.37, 'EUR': 1.16, 'CAD': 1.71, 'AUD': 1.85, 'JPY': 150.68, 'CHF': 1.26, 'CNY': 8.84, 'INR': 102.74 },
  'CAD': { 'USD': 0.80, 'EUR': 0.68, 'GBP': 0.58, 'AUD': 1.08, 'JPY': 88.00, 'CHF': 0.74, 'CNY': 5.16, 'INR': 60.00 },
  'AUD': { 'USD': 0.74, 'EUR': 0.63, 'GBP': 0.54, 'CAD': 0.93, 'JPY': 81.48, 'CHF': 0.68, 'CNY': 4.78, 'INR': 55.56 },
  'JPY': { 'USD': 0.009, 'EUR': 0.008, 'GBP': 0.007, 'CAD': 0.011, 'AUD': 0.012, 'CHF': 0.008, 'CNY': 0.059, 'INR': 0.68 },
  'CHF': { 'USD': 1.09, 'EUR': 0.93, 'GBP': 0.79, 'CAD': 1.35, 'AUD': 1.47, 'JPY': 119.57, 'CNY': 7.01, 'INR': 81.52 },
  'CNY': { 'USD': 0.15, 'EUR': 0.13, 'GBP': 0.11, 'CAD': 0.19, 'AUD': 0.21, 'JPY': 17.05, 'CHF': 0.14, 'INR': 11.63 },
  'INR': { 'USD': 0.013, 'EUR': 0.011, 'GBP': 0.010, 'CAD': 0.017, 'AUD': 0.018, 'JPY': 1.47, 'CHF': 0.012, 'CNY': 0.086 }
};

// Convert amount from one currency to another
export const convertCurrency = (amount, fromCurrency, toCurrency) => {
  if (fromCurrency === toCurrency) return amount;
  if (!amount || amount <= 0) return 0;
  
  const rate = EXCHANGE_RATES[fromCurrency]?.[toCurrency];
  if (!rate) {
    console.warn(`No exchange rate found for ${fromCurrency} to ${toCurrency}`);
    return amount; // Return original amount if no rate found
  }
  
  return Math.round(amount * rate * 100) / 100; // Round to 2 decimal places
};

// Convert multiple amounts to a target currency
export const convertAmountsToCurrency = (amounts, targetCurrency) => {
  return amounts.map(({ amount, currency, ...rest }) => ({
    ...rest,
    amount,
    currency,
    convertedAmount: convertCurrency(amount, currency, targetCurrency),
    targetCurrency
  }));
};

// Calculate total from multiple currency amounts
export const calculateTotalInCurrency = (amounts, targetCurrency) => {
  return amounts.reduce((total, { amount, currency }) => {
    const convertedAmount = convertCurrency(amount, currency, targetCurrency);
    return total + convertedAmount;
  }, 0);
};

// Convert budget summary to user's preferred currency
export const convertBudgetSummary = (budgetAmount, budgetCurrency, userPreferredCurrency) => {
  if (budgetCurrency === userPreferredCurrency) {
    return {
      amount: budgetAmount,
      currency: budgetCurrency,
      symbol: getCurrencySymbol(budgetCurrency)
    };
  }
  
  const convertedAmount = convertCurrency(budgetAmount, budgetCurrency, userPreferredCurrency);
  return {
    amount: convertedAmount,
    currency: userPreferredCurrency,
    symbol: getCurrencySymbol(userPreferredCurrency)
  };
};

// Convert individual costs to user's preferred currency for budget summary
export const convertCostsForBudgetSummary = (costs, userPreferredCurrency) => {
  return costs.map(cost => ({
    ...cost,
    convertedAmount: convertCurrency(cost.amount, cost.currency, userPreferredCurrency),
    targetCurrency: userPreferredCurrency
  }));
};
